import { Group, Loader, Tooltip } from "@mantine/core";
import { useState } from "react";
import {
  FiArchive,
  FiCode,
  FiFile,
  FiFileText,
  FiFilm,
  FiGrid,
  FiImage,
  FiMonitor,
  FiMusic,
  FiX,
} from "react-icons/fi";
import { getActionAttachmentDownloadUrl } from "api";
import { notify } from "utils/common";
import { formatAttachmentSize } from "utils/attachmentUploads";
import css from "./ActionAttachmentBadges.module.scss";

const getAttachmentIcon = (attachment) => {
  const contentType = String(attachment?.contentType || "").toLowerCase();
  const extension = String(attachment?.name || "")
    .split(".")
    .pop()
    .toLowerCase();

  if (contentType.startsWith("image/")) return FiImage;
  if (contentType.startsWith("video/")) return FiFilm;
  if (contentType.startsWith("audio/")) return FiMusic;
  if (contentType.includes("zip") || contentType.includes("compressed")) {
    return FiArchive;
  }

  if (["png", "jpg", "jpeg", "gif", "webp", "svg", "heic"].includes(extension)) {
    return FiImage;
  }
  if (["mp4", "mov", "avi", "mkv", "webm"].includes(extension)) return FiFilm;
  if (["mp3", "wav", "aac", "m4a", "flac"].includes(extension)) return FiMusic;
  if (["zip", "rar", "7z", "tar", "gz"].includes(extension)) return FiArchive;
  if (["xls", "xlsx", "csv", "numbers"].includes(extension)) return FiGrid;
  if (["ppt", "pptx", "key"].includes(extension)) return FiMonitor;
  if (
    [
      "js",
      "jsx",
      "ts",
      "tsx",
      "json",
      "html",
      "css",
      "scss",
      "xml",
      "yaml",
      "yml",
    ].includes(extension)
  ) {
    return FiCode;
  }
  if (["pdf", "doc", "docx", "txt", "md", "rtf"].includes(extension)) {
    return FiFileText;
  }

  return FiFile;
};

const ActionAttachmentBadges = ({
  attachments = [],
  actionId,
  actionOwnerUid,
  collectionName = "ActionItems",
  removable = false,
  onRemove,
}) => {
  const [openingId, setOpeningId] = useState(null);

  if (!attachments.length) return null;

  const getUploadProgressLabel = (attachment) => {
    if (!attachment?.uploading) return null;
    const progress = Number(attachment.progress);
    if (!Number.isFinite(progress) || progress <= 0) return "Uploading";
    return `${Math.min(100, Math.max(0, Math.round(progress)))}%`;
  };

  const openAttachment = async (attachment) => {
    if (!attachment?.storagePath || removable) return;

    setOpeningId(attachment.id || attachment.storagePath);
    const targetWindow = window.open("", "_blank");
    if (targetWindow) targetWindow.opener = null;

    try {
      const response = await getActionAttachmentDownloadUrl({
        actionId,
        actionOwnerUid,
        collectionName,
        storagePath: attachment.storagePath,
      });

      if (!response?.url) {
        targetWindow?.close();
        notify("Unable to open attachment", "error");
        return;
      }

      if (targetWindow) {
        targetWindow.location.href = response.url;
      } else {
        window.open(response.url, "_blank", "noopener,noreferrer");
      }
    } catch (error) {
      targetWindow?.close();
      console.error("Error opening action attachment:", error);
      notify("Unable to open attachment", "error");
    } finally {
      setOpeningId(null);
    }
  };

  return (
    <Group spacing={6} className={css.attachments}>
      {attachments.map((attachment, index) => {
        const attachmentKey =
          attachment.id || attachment.storagePath || `${attachment.name}-${index}`;
        const isOpening = openingId === attachmentKey;
        const canOpen = !removable && Boolean(attachment?.storagePath);
        const label = `${attachment.name || "Attachment"} (${formatAttachmentSize(
          attachment.size
        )})`;
        const Icon = getAttachmentIcon(attachment);
        const isUploading = Boolean(attachment?.uploading);
        const uploadProgressLabel = getUploadProgressLabel(attachment);

        return (
          <Tooltip key={attachmentKey} label={label} width="auto">
            <span
              className={`${css.badge} ${canOpen ? css.clickable : ""} ${
                isUploading ? css.uploading : ""
              }`}
              onClick={() => openAttachment(attachment)}
              role={canOpen ? "button" : undefined}
              tabIndex={canOpen ? 0 : undefined}
              onKeyDown={(event) => {
                if (!canOpen) return;
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  openAttachment(attachment);
                }
              }}
              title={isUploading ? "Uploading..." : isOpening ? "Opening..." : label}
            >
              {isUploading ? (
                <Loader className={css.uploadSpinner} size={14} color="gray" />
              ) : (
                <Icon className={css.fileIcon} size={14} aria-hidden="true" />
              )}
              <span className={css.fileName}>
                {isOpening ? "Opening..." : attachment.name || "Attachment"}
              </span>
              {uploadProgressLabel && (
                <span className={css.uploadProgress}>{uploadProgressLabel}</span>
              )}
              {removable && !isUploading && (
                <button
                  type="button"
                  className={css.removeButton}
                  aria-label={`Remove ${attachment.name || "attachment"}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onRemove?.(attachment);
                  }}
                >
                  <FiX size={13} />
                </button>
              )}
            </span>
          </Tooltip>
        );
      })}
    </Group>
  );
};

export default ActionAttachmentBadges;
